import {
  Component,
  inject,
  OnDestroy,
  OnInit,
  signal,
  WritableSignal,
  ChangeDetectionStrategy,
} from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { ConfirmationService } from '@openng/optimus-ui/api';
import {
  DynamicDialogConfig,
  DynamicDialogRef,
} from '@openng/optimus-ui/dynamicdialog';
import { Facility } from 'src/app/model/locations/facility';
import { AuthApiService } from 'src/app/services/api/auth-api.service';
import { FacilityApiService } from 'src/app/services/api/facility-api.service';
import { FacilityBuilderService } from 'src/app/services/builder/facility-builder.service';
import { LogAndMessageService } from 'src/app/services/log-and-message.service';
import { FacilityNotepadEntry } from 'src/app/model/locations/facility-notepad-entry';
import { sortByProperty, SortDirection } from 'src/app/utils/utils';
import { LogEntryComponent } from 'src/app/core/components/log-entry.component';

@Component({
  selector: 'app-facility-notes-dialog',
  imports: [ReactiveFormsModule, TranslateModule, LogEntryComponent],
  templateUrl: './facility-notes-dialog.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './facility-notes-dialog.component.scss',
})
export class FacilityNotesDialogComponent implements OnInit, OnDestroy {
  config = inject(DynamicDialogConfig);
  fb = inject(FormBuilder);
  authApi = inject(AuthApiService);
  facilityBuilder = inject(FacilityBuilderService);
  facilityApi = inject(FacilityApiService);
  ref = inject(DynamicDialogRef);
  logAndMessage = inject(LogAndMessageService);
  confirmationService = inject(ConfirmationService);
  translateService = inject(TranslateService);

  facility: WritableSignal<Facility | null> = signal(null);
  facilityNotepadEntries: WritableSignal<FacilityNotepadEntry[]> = signal([]);
  form: FormGroup;
  formWorking = signal(false);
  disabled: boolean = false;
  showNotes: boolean = false;
  showNotepadEntries: boolean = false;

  constructor() {
    this.form = this.fb.group({
      facilityName: [null],
      facilityUid: [null],
      facilityNotes: [null],
    });
  }

  ngOnInit(): void {
    this.facility.set(this.config?.data?.facility);
    this.facilityNotepadEntries.set(this.config?.data?.facilityNotepadEntries);
    this.updateForm();
  }

  updateForm(): void {
    const currentFacility = this.facility();
    this.form.patchValue({
      facilityName: currentFacility?.facilityName,
      facilityUid: currentFacility?.facilityUid,
      facilityNotes: currentFacility?.notes,
    });
    this.facilityNotepadEntries.set(
      this.facilityNotepadEntries().sort((a, b) =>
        sortByProperty(a, b, 'displayDate', SortDirection.DESCENDING)
      )
    );

    if (this.facilityNotepadEntries().length > 0) {
      this.showNotepadEntries = true;
    } else {
      this.showNotepadEntries = false;
    }

    if (currentFacility?.notes?.length ?? 0 > 0) {
      this.showNotes = true;
    } else {
      this.showNotes = false;
    }

    this.form.disable();
    this.form.markAsPristine();
  }

  ngOnDestroy(): void {
    if (this.ref) {
      this.ref.close();
    }
  }
}
